import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Highlighter from 'react-highlight-words';
import Spinner from '@mos-front-components/public/es/components/Spinner';
import MosSelect from '@mos-front-components/public/es/components/MosSelect';
import useOnClickOutside from 'use-onclickoutside';

import './RegPlaceName.pcss';

import { ACT_RECORD_FIELD_NAMES } from '../../../../constants/actRecord';

const name = ACT_RECORD_FIELD_NAMES.regPlace;

const RegPlaceName = ({
  suggestions,
  fetching,
  fetchingError,
  value,
  error,
  onChange,
  onBlur,
  saveDraft
}) => {
  const [focused, setFocused] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const selectRef = useRef(null);
  const popupRef = useRef(null);

  const handleInputFocus = () => {
    setFocused(true);
  };

  const handleInputBlur = () => {
    if (!popupVisible) {
      setFocused(false);
    }

    onBlur(ACT_RECORD_FIELD_NAMES.regPlace);
    saveDraft();
  };

  const handleClickOutsidePopup = event => {
    const { target } = event;

    if (popupVisible && !target.closest('.RegPlaceName')) {
      setPopupVisible(false);
    }
  };

  useOnClickOutside(popupRef, handleClickOutsidePopup);

  useEffect(() => {
    if (focused && selectRef.current.inputRef) {
      selectRef.current.inputRef.current.focus();
    }
  }, [focused, selectRef.current]);

  useEffect(() => {
    if (selectRef.current && selectRef.current.inputRef) {
      selectRef.current.inputRef.current.addEventListener(
        'focus',
        handleInputFocus,
        true
      );
    }

    return () => {
      if (selectRef.current && selectRef.current.inputRef) {
        selectRef.current.inputRef.current.removeEventListener(
          'focus',
          handleInputFocus,
          true
        );
      }
    };
  }, [selectRef.current]);

  useEffect(() => {
    if (fetching) {
      setPopupVisible(true);

      if (selectRef.current.inputRef) {
        selectRef.current.inputRef.current.focus();
      }
    } else {
      setPopupVisible(focused);
    }
  }, [focused, fetching, selectRef.current]);

  const renderSuggestion = (suggestion, { query }) => (
    <Highlighter
      searchWords={[query]}
      autoEscape
      highlightStyle={{ fontWeight: 'bold', background: 'none' }}
      textToHighlight={suggestion.text}
    />
  );

  const normalizeText = (text = '') => {
    return text.replace(/\s\s|\*/gim, '');
  };

  const getText = () => {
    if (typeof value === 'object' && value !== null) {
      return normalizeText(value.text) || '';
    }

    return '';
  };

  const makeChange = (text, method) => {
    if (method !== 'click') {
      setFocused(true);
      onChange({
        target: {
          name,
          value: {
            text
          }
        }
      });
    }

    if (!method) {
      onBlur(ACT_RECORD_FIELD_NAMES.regPlace);
    }
  };

  const handleChange = newValue => {
    onChange({
      target: {
        name,
        value: {
          ...newValue,
          skipSearch: true
        }
      }
    });
  };

  const handleFindRegPlace = () => {
    handleChange({
      text: getText()
    });
    setFocused(true);
  };

  const text = getText();

  const searchEnabled = text && text.length > 2;
  const normSuggestions =
    suggestions?.map(item => ({
      text: normalizeText(item.name),
      value: item.code
    })) || [];
  const showNotFound =
    searchEnabled &&
    suggestions?.length === 0 &&
    fetching === false &&
    !fetchingError;
  const showPopup = popupVisible && (fetching || fetchingError || showNotFound);

  const classNames = cn({
    RegPlaceName__Select: true,
    MosSelect__Container_error: fetchingError
  });

  return (
    <div className="RegPlaceName">
      <MosSelect
        placeholder="Наименование органа ЗАГС"
        className={classNames}
        value={text || ''}
        name={name}
        suggestions={searchEnabled && !showPopup ? normSuggestions : []}
        renderSuggestion={renderSuggestion}
        hasError={error}
        onChange={handleChange}
        onInputChange={makeChange}
        onBlur={handleInputBlur}
        withClear
        ref={selectRef}
      />
      {showPopup && (
        <div ref={popupRef} className="RegPlaceName__popup">
          {fetching && (
            <div className="RegPlaceName__loading">
              <Spinner />
            </div>
          )}
          {showNotFound && (
            <div className="RegPlaceName__notFound">
              <div className="RegPlaceName__error-message">
                Не можем найти орган ЗАГС с таким названием
              </div>
            </div>
          )}
          {fetchingError && (
            <div className="RegPlaceName__error">
              <div className="RegPlaceName__error-message">
                По техническим причинам не удалось найти орган ЗАГС в списке
              </div>
              <button
                type="button"
                onClick={handleFindRegPlace}
                className="RegPlaceName__button"
              >
                Повторить поиск
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const regPlacesPropTypes = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.shape),
  PropTypes.string
]);

RegPlaceName.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      text: PropTypes.string,
      selected: PropTypes.bool,
      value: PropTypes.string
    })
  ]),
  fetching: PropTypes.bool,
  fetchingError: PropTypes.bool,
  error: PropTypes.bool,
  suggestions: regPlacesPropTypes,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onFindRegPlace: PropTypes.func.isRequired,
  saveDraft: PropTypes.func.isRequired
};

RegPlaceName.defaultProps = {
  value: { text: '' },
  error: false,
  suggestions: null,
  fetching: false,
  fetchingError: false
};

export default RegPlaceName;
